import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Plane, Menu, X, Youtube } from "lucide-react";

const YT_URL = "https://www.youtube.com/channel/UCoaeo2zs2N6zx3UUyEzv08Q";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/crew-rostering", label: "Crew Rostering" },
  { to: "/ground-ops", label: "Ground Ops" },
  { to: "/analytics", label: "Analytics" },
  { to: "/flight-ops", label: "Flight Ops" },
  { to: "/compliance", label: "Compliance" },
];

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 marketing-font-body
      ${scrolled ? "bg-[#07111f]/95 backdrop-blur-md border-b border-white/5 shadow-lg" : "bg-transparent"}`}
      data-testid="marketing-nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group shrink-0">
          <div className="w-7 h-7 bg-orange-500/20 border border-orange-500/40 rounded flex items-center justify-center">
            <Plane size={13} className="text-orange-400" />
          </div>
          <div className="leading-none">
            <div className="text-white font-bold text-[13px] marketing-font-heading tracking-tight">Fatigue Shield</div>
            <div className="text-orange-400 text-[9px] font-semibold tracking-[0.15em] uppercase marketing-font-mono">+ GroundShield</div>
          </div>
        </Link>

        {/* Desktop nav — 6 items */}
        <div className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} end={to === "/"}
              className={({ isActive }) =>
                `text-[13px] transition-colors ${isActive ? "text-orange-400 font-semibold" : "text-slate-400 hover:text-white"}`
              }>
              {label}
            </NavLink>
          ))}
        </div>

        {/* Right CTAs */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <a href={YT_URL} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-slate-500 hover:text-red-400 transition-colors"
            title="Watch on YouTube" data-testid="nav-youtube-link">
            <Youtube size={15} />
          </a>
          <Link to="/login" className="text-[13px] text-slate-400 hover:text-white transition-colors px-3 py-2">
            Sign In
          </Link>
          <Link to="/marketing/demo"
            className="bg-orange-500 hover:bg-orange-400 text-white text-[13px] font-bold px-4 py-2.5 transition-all hover:-translate-y-px"
            data-testid="nav-demo-btn">
            Request Demo
          </Link>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-slate-400 hover:text-white">
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#07111f] border-t border-white/5 px-6 py-4 space-y-2">
          {NAV_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)}
              className="block text-sm text-slate-300 hover:text-white py-2 border-b border-white/5 transition-colors">
              {label}
            </Link>
          ))}
          <a href={YT_URL} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 py-2 transition-colors">
            <Youtube size={14} /> Watch on YouTube
          </a>
          <div className="flex gap-3 pt-2">
            <Link to="/login" onClick={() => setMenuOpen(false)}
              className="flex-1 text-center text-sm border border-white/15 text-slate-300 py-2.5 transition-colors hover:border-white/30">
              Sign In
            </Link>
            <Link to="/marketing/demo" onClick={() => setMenuOpen(false)}
              className="flex-1 text-center text-sm bg-orange-500 hover:bg-orange-400 text-white font-bold py-2.5 transition-colors">
              Request Demo
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export function MarketingFooter() {
  return (
    <footer className="bg-[#04090f] border-t border-white/5 marketing-font-body">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        {/* Brand */}
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 bg-orange-500/20 border border-orange-500/40 rounded flex items-center justify-center">
              <Plane size={13} className="text-orange-400" />
            </div>
            <span className="text-white font-bold text-sm marketing-font-heading">Fatigue Shield</span>
          </div>
          <p className="text-[12px] text-slate-600 leading-relaxed">
            AI-powered aviation operations and ground execution platform for SME airlines.
          </p>
          <div className="mt-4 text-[10px] text-slate-700 marketing-font-mono uppercase tracking-wider">
            Built for ramp. Trusted for safety.
          </div>
        </div>

        {/* Platform */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold marketing-font-mono mb-4">Platform</div>
          {[{ to: "/marketing/platform", l: "Overview" },
            { to: "/marketing/module/turnaround-control", l: "Turnaround Control" },
            { to: "/marketing/module/ramp-app", l: "Ramp Mobile App" },
            { to: "/marketing/module/delay-prediction", l: "AI Delay Prediction" },
          ].map(i => <Link key={i.to} to={i.to} className="block text-sm text-slate-500 hover:text-slate-300 py-1 transition-colors">{i.l}</Link>)}
        </div>

        {/* Modules */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold marketing-font-mono mb-4">GroundShield</div>
          {[{ to: "/marketing/module/vendor-sla", l: "Vendor SLA" },
            { to: "/marketing/module/staff-planning", l: "Staff Planning" },
            { to: "/marketing/module/ground-fatigue", l: "Ground Fatigue" },
            { to: "/marketing/module/crew-fatigue", l: "Crew FRMS" },
          ].map(i => <Link key={i.to} to={i.to} className="block text-sm text-slate-500 hover:text-slate-300 py-1 transition-colors">{i.l}</Link>)}
        </div>

        {/* Contact */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold marketing-font-mono mb-4">Contact</div>
          <Link to="/marketing/demo" className="block text-sm text-orange-400 hover:text-orange-300 py-1 transition-colors font-bold">Request a Demo</Link>
          <Link to="/login" className="block text-sm text-slate-500 hover:text-slate-300 py-1 transition-colors">Operator Login</Link>
          <a href={YT_URL} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-400 py-1 transition-colors mt-1">
            <Youtube size={13} /> Watch on YouTube
          </a>
          <div className="mt-4 text-[11px] text-slate-700 leading-relaxed">
            Designed for Indian SME airlines.<br />
            DGCA fatigue rules built-in.
          </div>
        </div>
      </div>
      <div className="border-t border-white/5 max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <span className="text-[11px] text-slate-700">© 2026 Fatigue Shield. All rights reserved.</span>
        <div className="flex items-center gap-4">
          <a href={YT_URL} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-red-400 transition-colors marketing-font-mono uppercase tracking-wider"
            data-testid="footer-youtube-link">
            <Youtube size={12} /> YouTube
          </a>
          <span className="text-[10px] text-slate-700 marketing-font-mono uppercase tracking-wider">GroundShield by Fatigue Shield</span>
        </div>
      </div>
    </footer>
  );
}
